import { ReactNode, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { TOAST_SUCCESS_STYLE } from 'config/constants';
import useAsyncEffect from 'use-async-effect';
import classNames from 'classnames';
import { Layout } from 'components/Layout/Layout';
import { MobileLayout } from 'components/mobile/MobileLayout/MobileLayout';
import { Loader } from 'components/Loader/Loader';
import { UserContext } from 'context/userContext';
import { useBreakpoint } from 'hooks/useBreakpoint';
import { intlLocaleOf } from 'config/languages';
import {
  BettingLimit,
  LimitKind,
  LimitPeriod,
  LimitsResponse,
  getLimits,
  removeLimit,
  setLimit,
} from 'services/api/responsibleService';
import styles from '../styles/pages/Responsible.module.css';

const CURRENCY = process.env.REACT_APP_CURRENCY ?? '';
const KINDS: LimitKind[] = ['stake', 'loss'];
const PERIODS: LimitPeriod[] = ['day', 'week', 'month'];

/** Frame shared by the responsible-gaming pages on phone and desktop. */
export function ResponsibleFrame({
  titleKey,
  children,
}: {
  titleKey: string;
  children: ReactNode;
}) {
  const { isMobile } = useBreakpoint();
  const { t } = useTranslation();
  const body = <div className={styles.page}>{children}</div>;
  return isMobile ? (
    <MobileLayout pageTitle={titleKey}>{body}</MobileLayout>
  ) : (
    <Layout>
      <div className={styles.desktop}>
        <h1 className={styles.heading}>{t(titleKey)}</h1>
        {body}
      </div>
    </Layout>
  );
}

function formatDate(iso: string | undefined, lang: string) {
  if (!iso) return '';
  return new Date(iso).toLocaleString(intlLocaleOf(lang), {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

function LimitCard({
  limit,
  onRemove,
  busy,
}: {
  limit: BettingLimit;
  onRemove: () => void;
  busy: boolean;
}) {
  const { t, i18n } = useTranslation();
  const share = limit.amount > 0 ? Math.min(1, limit.used / limit.amount) : 0;
  const left = Math.max(0, limit.amount - limit.used);
  return (
    <div className={styles.limitCard}>
      <div className={styles.limitHead}>
        <span>
          <strong>{t(`limit_kind_${limit.kind}`)}</strong>
          <small>{t(`limit_period_${limit.period}`)}</small>
        </span>
        <span className={styles.limitAmount}>
          {limit.amount.toFixed(2)} {CURRENCY}
        </span>
      </div>
      <div className={styles.bar} aria-hidden>
        <span
          className={classNames(share >= 1 && styles.barFull)}
          style={{ width: `${share * 100}%` }}
        />
      </div>
      <div className={styles.limitFoot}>
        <span>
          {t('limit_used', {
            used: limit.used.toFixed(2),
            currency: CURRENCY,
          })}
        </span>
        <span>
          {t('limit_left', { left: left.toFixed(2), currency: CURRENCY })}
        </span>
      </div>
      {limit.pending_remove && (
        <p className={styles.pending}>
          {t('limit_pending_remove', {
            date: formatDate(limit.pending_from, i18n.language),
          })}
        </p>
      )}
      {limit.pending_amount != null && (
        <p className={styles.pending}>
          {t('limit_pending_raise', {
            amount: limit.pending_amount.toFixed(2),
            currency: CURRENCY,
            date: formatDate(limit.pending_from, i18n.language),
          })}
        </p>
      )}
      {!limit.pending_remove && (
        <button
          type="button"
          className={styles.linkButton}
          disabled={busy}
          onClick={onRemove}
        >
          {t('limit_remove')}
        </button>
      )}
    </div>
  );
}

export const BettingLimits = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { loggedUser } = useContext(UserContext);
  const [state, setState] = useState<LimitsResponse | null>(null);
  const [kind, setKind] = useState<LimitKind | ''>('');
  const [period, setPeriod] = useState<LimitPeriod | ''>('');
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  useAsyncEffect(async () => {
    if (!loggedUser) return;
    try {
      setState(await getLimits());
    } catch {
      setFailed(true);
    }
  }, [loggedUser?.id]);

  if (!loggedUser) {
    navigate('/login');
    return null;
  }

  const days = state?.protection_days ?? 7;
  const value = Number(amount.replace(',', '.'));
  const canSave = !!kind && !!period && Number.isFinite(value) && value > 0;
  const existing = state?.limits.find(
    (l) => l.kind === kind && l.period === period
  );

  const save = async () => {
    if (!canSave || busy) return;
    setBusy(true);
    try {
      const next = await setLimit(
        kind as LimitKind,
        period as LimitPeriod,
        value
      );
      setState(next);
      const raised = existing && value > existing.amount;
      toast(t(raised ? 'limit_saved_pending' : 'limit_saved', { days }), {
        style: TOAST_SUCCESS_STYLE,
      });
      setAmount('');
    } catch {
      toast(t('limit_save_error'));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (l: BettingLimit) => {
    if (busy) return;
    if (!window.confirm(t('limit_remove_confirm', { days }))) return;
    setBusy(true);
    try {
      setState(await removeLimit(l.kind, l.period));
    } catch {
      toast(t('limit_save_error'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ResponsibleFrame titleKey="betting_limits_label">
      <div className={styles.info}>
        <span className={styles.infoIcon}>i</span>
        <ul>
          <li>{t('limits_info_what')}</li>
          <li>{t('limits_info_periods')}</li>
          <li>{t('limits_info_lower')}</li>
          <li>{t('limits_info_raise', { days })}</li>
        </ul>
      </div>

      {failed && <p className={styles.error}>{t('limit_load_error')}</p>}
      {!state && !failed && <Loader />}

      {state && state.limits.length > 0 && (
        <>
          <p className={styles.sectionTitle}>{t('limits_active')}</p>
          {state.limits.map((l) => (
            <LimitCard
              key={`${l.kind}-${l.period}`}
              limit={l}
              busy={busy}
              onRemove={() => remove(l)}
            />
          ))}
        </>
      )}

      <p className={styles.sectionTitle}>{t('limit_new')}</p>
      <div className={styles.form}>
        <label className={styles.field}>
          <span>{t('limit_type_label')}</span>
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value as LimitKind)}
          >
            <option value="">{t('select_placeholder')}</option>
            {KINDS.map((k) => (
              <option key={k} value={k}>
                {t(`limit_kind_${k}`)}
              </option>
            ))}
          </select>
        </label>
        <label className={styles.field}>
          <span>{t('limit_interval_label')}</span>
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value as LimitPeriod)}
          >
            <option value="">{t('select_placeholder')}</option>
            {PERIODS.map((p) => (
              <option key={p} value={p}>
                {t(`limit_period_${p}`)}
              </option>
            ))}
          </select>
        </label>
        <label className={styles.field}>
          <span>{t('limit_amount_label')}</span>
          <div className={styles.amount}>
            <em>{CURRENCY}</em>
            <input
              inputMode="decimal"
              placeholder="0.00"
              value={amount}
              onChange={(e) =>
                setAmount(e.target.value.replace(/[^\d.,]/g, ''))
              }
            />
          </div>
        </label>
        {existing && canSave && value > existing.amount && (
          <p className={styles.pending}>{t('limit_raise_warning', { days })}</p>
        )}
        <button
          type="button"
          className={styles.primary}
          disabled={!canSave || busy}
          onClick={save}
        >
          {existing ? t('limit_change') : t('limit_set')}
        </button>
      </div>
    </ResponsibleFrame>
  );
};
